import { useCallback, useEffect, useState } from 'react';
import {
  WORKSPACE_ACTIONS,
  uuidV4,
  type VersionEntry,
  type WorkspaceActionAvailability,
  type WorkspaceEnvironmentDeclaration,
  type WorkspaceProjection,
} from '@localagent/sdk';
import { Badge } from '@localagent/ui/components/badge';
import { Button } from '@localagent/ui/components/button';
import { getSDK } from '../../services/sdk-instance';
import {
  useBindWorkspace,
  useCancelWorkspaceOperation,
  useConvertWorkspace,
  useRetryWorkspaceArchiveCleanup,
  useSaveWorkspaceEnvironment,
  useSleepWorkspace,
  useTransferWorkspace,
  useUnbindWorkspace,
  useWakeWorkspace,
  useWorkspaceOperationHistory,
  useWorkspaceProjection,
} from '../../hooks/useWorkspaces';
import { useSessions } from '../../hooks/useSessions';
import { WorkspaceResourceCards, WorkspaceResourceSummary } from './WorkspaceResourceCards';
import { actionLabel, displayValue } from './workspaceDisplay';
import { LoadingState } from '@localagent/ui/components/shogun-spinner';

interface WorkspaceStatePanelProps {
  workspaceId: string;
}

function ActionAvailability({
  action,
  availability,
}: {
  action: string;
  availability: WorkspaceActionAvailability;
}) {
  return (
    <li className="rounded-md p-3" data-testid={`workspace-action-${action}`}>
      <div className="flex items-center justify-between gap-3">
        <span className="font-medium">{actionLabel(action)}</span>
        <Badge variant={availability.available ? 'success' : 'secondary'}>
          {availability.available ? 'Available' : 'Unavailable'}
        </Badge>
      </div>
      {availability.refusal && (
        <div className="mt-2 text-body" data-testid={`workspace-action-refusal-${action}`}>
          <p className="font-medium text-destructive">
            {displayValue(availability.refusal.reason)}
          </p>
          <p className="text-muted-foreground">{availability.refusal.message}</p>
          {availability.refusal.operation_id && (
            <p className="mt-1 font-mono text-caption text-muted-foreground">
              Operation {availability.refusal.operation_id}
            </p>
          )}
        </div>
      )}
    </li>
  );
}

export function WorkspaceProjectionStatus({ projection }: { projection: WorkspaceProjection }) {
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="workspace-projection-status">
      <Badge variant="outline">{displayValue(projection.lifecycle)}</Badge>
      <Badge variant="secondary">{displayValue(projection.assignment.routing_class)}</Badge>
      <WorkspaceResourceSummary projection={projection} />
      <Badge
        variant={projection.index.health === 'ok' ? 'success' : 'destructive'}
        data-testid="workspace-projection-index-health"
      >
        Search {displayValue(projection.index.health)} · {displayValue(projection.index.progress)}
      </Badge>
      {projection.filesystem.failure_reason && (
        <span className="text-body text-destructive">{projection.filesystem.failure_reason}</span>
      )}
      {projection.workstation.failure_reason && (
        <span className="text-body text-destructive">{projection.workstation.failure_reason}</span>
      )}
    </div>
  );
}

export function WorkspaceStatePanel({ workspaceId }: WorkspaceStatePanelProps) {
  const { projection, deviceId, isLoading, error, refreshRequested, revisionGap } =
    useWorkspaceProjection(workspaceId);

  return (
    <WorkspaceStatePanelContent
      projection={projection}
      deviceId={deviceId}
      isLoading={isLoading}
      error={error}
      refreshRequested={refreshRequested}
      revisionGap={revisionGap}
    />
  );
}

export function WorkspaceStatePanelContent({
  projection,
  deviceId,
  isLoading,
  error,
  refreshRequested,
  revisionGap,
}: {
  projection: WorkspaceProjection | null;
  deviceId: string | null;
  isLoading: boolean;
  error: unknown;
  refreshRequested: boolean;
  revisionGap: { from: number; to: number } | null;
}) {
  const operationHistory = useWorkspaceOperationHistory(
    projection?.workspace_id ?? null,
    projection?.revision ?? 0
  );

  if (!projection && isLoading) {
    return <LoadingState label="Loading workspace state…" />;
  }

  if (!projection) {
    return (
      <div className="rounded-md bg-destructive/10 p-4 text-body text-destructive">
        {error instanceof Error ? error.message : 'Workspace state is unavailable'}
      </div>
    );
  }

  return (
    <section
      className="rounded-lg bg-card p-4"
      data-testid="workspace-state-panel"
      data-bridge-device-id={deviceId ?? undefined}
      data-workspace-revision={projection.revision}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold">Workspace state</h3>
          <p className="mt-1 text-body text-muted-foreground">
            Backend revision {projection.revision}
          </p>
        </div>
        <WorkspaceProjectionStatus projection={projection} />
      </div>

      {refreshRequested && revisionGap && (
        <p
          className="mt-3 rounded-md bg-muted px-3 py-2 text-body"
          data-testid="workspace-state-refresh-requested"
        >
          Refreshing after missing revisions {revisionGap.from + 1}–{revisionGap.to - 1}.
        </p>
      )}

      {error != null && (
        <p className="mt-3 rounded-md bg-destructive/10 px-3 py-2 text-body text-destructive">
          {error instanceof Error ? error.message : 'Workspace state subscription failed'}
        </p>
      )}

      {projection.operation && (
        <div className="mt-4 rounded-md bg-muted p-3 text-body" data-testid="workspace-operation">
          <span className="font-medium">{actionLabel(projection.operation.kind)}</span>
          <span className="ml-2 text-muted-foreground">
            {displayValue(projection.operation.state)}
          </span>
          <p className="mt-1 font-mono text-caption text-muted-foreground">
            Operation {projection.operation.id}
          </p>
          {projection.operation.reason && (
            <p className="mt-1 font-medium text-destructive">
              {displayValue(projection.operation.reason)}
            </p>
          )}
          {projection.operation.message && <p className="mt-1">{projection.operation.message}</p>}
        </div>
      )}

      {operationHistory.data?.operations.some((operation) => operation.state !== 'running') && (
        <div className="mt-4 rounded-md p-3" data-testid="workspace-operation-history">
          <h4 className="text-label font-semibold">Recent operation outcomes</h4>
          <ul className="mt-2 space-y-2">
            {operationHistory.data.operations
              .filter((operation) => operation.state !== 'running')
              .map((operation) => (
                <li
                  key={operation.id}
                  className="rounded-md bg-muted p-2 text-body"
                  data-testid={`workspace-operation-history-${operation.id}`}
                >
                  <span className="font-medium">{actionLabel(operation.kind)}</span>
                  <span className="ml-2 text-muted-foreground">
                    {displayValue(operation.state)}
                  </span>
                  {operation.reason && (
                    <p className="mt-1 font-medium text-destructive">
                      {displayValue(operation.reason)}
                    </p>
                  )}
                  {operation.message && <p className="mt-1">{operation.message}</p>}
                </li>
              ))}
          </ul>
        </div>
      )}

      <WorkspaceResourceCards projection={projection} />

      <div className="mt-4">
        <h4 className="text-label font-semibold">Actions</h4>
        <ul className="mt-2 grid gap-2 sm:grid-cols-2">
          {WORKSPACE_ACTIONS.map((action) => {
            const availability = projection.actions[action];
            return availability ? (
              <ActionAvailability key={action} action={action} availability={availability} />
            ) : null;
          })}
        </ul>
      </div>

      <WorkspaceLifecycleControls projection={projection} />
      {projection.assignment.routing_class === 'cloud' && (
        <WorkspaceEnvironmentControls projection={projection} />
      )}
      {projection.capabilities.version_history && <WorkspaceLocalHistory projection={projection} />}
    </section>
  );
}

function mutationMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function WorkspaceLifecycleControls({ projection }: { projection: WorkspaceProjection }) {
  const [folderPath, setFolderPath] = useState(projection.binding?.folder_path ?? '');
  const [bindingDeviceId, setBindingDeviceId] = useState(
    projection.binding?.device_id ?? projection.workstation.device_id ?? ''
  );
  const [outcome, setOutcome] = useState<string | null>(null);
  const bind = useBindWorkspace(projection.workspace_id);
  const unbind = useUnbindWorkspace(projection.workspace_id);
  const transfer = useTransferWorkspace(projection.workspace_id);
  const convert = useConvertWorkspace(projection.workspace_id);
  const cancel = useCancelWorkspaceOperation(projection.workspace_id);
  const retryCleanup = useRetryWorkspaceArchiveCleanup(projection.workspace_id);
  const wake = useWakeWorkspace(projection.workspace_id);
  const sleep = useSleepWorkspace(projection.workspace_id);

  useEffect(() => {
    if (!projection.binding) return;
    setFolderPath(projection.binding.folder_path);
    setBindingDeviceId(projection.binding.device_id);
  }, [projection.binding]);

  const run = useCallback(async (action: () => Promise<WorkspaceProjection>, success: string) => {
    setOutcome(null);
    try {
      await action();
      setOutcome(success);
    } catch (actionError) {
      setOutcome(mutationMessage(actionError));
    }
  }, []);

  const pending =
    bind.isPending ||
    unbind.isPending ||
    transfer.isPending ||
    convert.isPending ||
    cancel.isPending ||
    retryCleanup.isPending ||
    wake.isPending ||
    sleep.isPending;
  const divergence = projection.binding?.divergence_resolution;
  const currentOperationId = projection.operation?.id;

  return (
    <div className="mt-4 pt-4" data-testid="workspace-lifecycle-controls">
      <h4 className="text-label font-semibold">Workspace actions</h4>
      {projection.binding?.state !== 'bound' && projection.actions.bind?.available && (
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          <input
            className="rounded-md bg-muted px-3 py-2 text-body"
            aria-label="Local device ID"
            data-testid="workspace-bind-device"
            value={bindingDeviceId}
            onChange={(event) => setBindingDeviceId(event.target.value)}
          />
          <input
            className="rounded-md bg-muted px-3 py-2 text-body"
            aria-label="Local folder path"
            data-testid="workspace-bind-folder"
            value={folderPath}
            onChange={(event) => setFolderPath(event.target.value)}
          />
          <Button
            className="sm:col-span-2"
            disabled={pending || !bindingDeviceId || !folderPath}
            data-testid="workspace-bind-submit"
            onClick={() =>
              void run(
                () =>
                  bind.mutateAsync({
                    deviceId: bindingDeviceId,
                    folderPath,
                    idempotencyKey: uuidV4(),
                  }),
                'Folder bound. No content was moved.'
              )
            }
          >
            Bind folder
          </Button>
        </div>
      )}

      {projection.binding && projection.actions.transfer?.available && (
        <div className="mt-2 flex gap-2">
          {(['push', 'pull'] as const).map((direction) => (
            <Button
              key={direction}
              variant="outline"
              disabled={pending}
              data-testid={`workspace-transfer-${direction}`}
              onClick={() =>
                void run(
                  () =>
                    transfer.mutateAsync({
                      deviceId: projection.binding!.device_id,
                      direction,
                      idempotencyKey: uuidV4(),
                    }),
                  `${actionLabel(direction)} transfer started.`
                )
              }
            >
              {actionLabel(direction)}
            </Button>
          ))}
        </div>
      )}

      {projection.binding && projection.actions.unbind?.available && (
        <Button
          className="mt-2"
          variant="outline"
          disabled={pending}
          data-testid="workspace-unbind"
          onClick={() =>
            void run(
              () =>
                unbind.mutateAsync({
                  deviceId: projection.binding!.device_id,
                  idempotencyKey: uuidV4(),
                }),
              'Folder unbound. No content was moved.'
            )
          }
        >
          Unbind folder
        </Button>
      )}

      {divergence && projection.binding && (
        <div
          className="mt-3 rounded-md bg-warning-subtle p-3"
          data-testid="workspace-divergence-resolution"
        >
          <p className="text-label font-medium">
            Both content locations changed. Choose which side wins.
          </p>
          <div className="mt-2 flex gap-2">
            {(['push', 'pull'] as const).map((direction) => (
              <Button
                key={direction}
                variant="outline"
                disabled={pending}
                data-testid={`workspace-resolve-divergence-${direction}`}
                onClick={() =>
                  void run(
                    () =>
                      transfer.mutateAsync({
                        deviceId: projection.binding!.device_id,
                        direction,
                        expectedSource: divergence[direction].expected_source,
                        expectedDestination: divergence[direction].expected_destination,
                        resolveDivergence: true,
                        idempotencyKey: uuidV4(),
                      }),
                    `${actionLabel(direction)} divergence resolution started.`
                  )
                }
              >
                Use {direction === 'push' ? 'local' : 'cloud'} content
              </Button>
            ))}
          </div>
        </div>
      )}

      {projection.assignment.routing_class === 'local' && projection.actions.convert?.available && (
        <Button
          className="mt-2"
          disabled={pending}
          data-testid="workspace-convert"
          onClick={() => void run(() => convert.mutateAsync(uuidV4()), 'Conversion started.')}
        >
          Convert to cloud
        </Button>
      )}

      {projection.actions.wake?.available && (
        <Button
          className="mt-2"
          disabled={pending}
          data-testid="workspace-wake"
          onClick={() => void run(() => wake.mutateAsync(uuidV4()), 'Workspace is waking.')}
        >
          Wake workstation
        </Button>
      )}

      {projection.actions.sleep?.available && (
        <Button
          className="mt-2"
          variant="outline"
          disabled={pending}
          data-testid="workspace-sleep"
          onClick={() =>
            void run(
              () => sleep.mutateAsync(uuidV4()),
              'Workspace is sleeping; durable content remains available.'
            )
          }
        >
          Sleep workstation
        </Button>
      )}

      {currentOperationId && projection.actions.cancel_operation?.available && (
        <Button
          className="mt-2"
          variant="outline"
          disabled={pending}
          data-testid="workspace-cancel-operation"
          onClick={() =>
            void run(
              () =>
                cancel.mutateAsync({
                  operationId: currentOperationId,
                  idempotencyKey: uuidV4(),
                }),
              `Cancellation requested for operation ${currentOperationId}.`
            )
          }
        >
          Cancel current operation
        </Button>
      )}

      {currentOperationId && projection.actions.retry_cleanup?.available && (
        <Button
          className="mt-2"
          disabled={pending}
          data-testid="workspace-retry-cleanup"
          onClick={() =>
            void run(
              () => retryCleanup.mutateAsync(currentOperationId),
              `Cleanup retry requested for operation ${currentOperationId}.`
            )
          }
        >
          Retry archive cleanup
        </Button>
      )}

      {outcome && (
        <p className="mt-2 text-body" data-testid="workspace-action-outcome">
          {outcome}
        </p>
      )}
    </div>
  );
}

const environmentExample: WorkspaceEnvironmentDeclaration = {
  image: 'registry.example.com/workspace@sha256:replace-with-64-hex-digest',
  features: [],
  packages: [],
  containerEnv: {},
  postCreateCommand: [],
};

function WorkspaceEnvironmentControls({ projection }: { projection: WorkspaceProjection }) {
  const [declaration, setDeclaration] = useState(() => JSON.stringify(environmentExample, null, 2));
  const [outcome, setOutcome] = useState<string | null>(null);
  const saveEnvironment = useSaveWorkspaceEnvironment(projection.workspace_id);
  const availability = projection.actions.environment_save;

  const save = useCallback(async () => {
    setOutcome(null);
    let parsed: WorkspaceEnvironmentDeclaration;
    try {
      parsed = JSON.parse(declaration) as WorkspaceEnvironmentDeclaration;
    } catch (parseError) {
      setOutcome(`Environment declaration must be valid JSON: ${mutationMessage(parseError)}`);
      return;
    }
    try {
      await saveEnvironment.mutateAsync({ declaration: parsed, idempotencyKey: uuidV4() });
      setOutcome(
        'Environment save started. The new revision will become active after its build succeeds.'
      );
    } catch (saveError) {
      setOutcome(mutationMessage(saveError));
    }
  }, [declaration, saveEnvironment]);

  return (
    <div className="mt-4 pt-4" data-testid="workspace-environment-controls">
      <h4 className="text-label font-semibold">Cloud environment</h4>
      {projection.workstation.environment_unsaved && (
        <p
          className="mt-2 rounded-md bg-warning-subtle px-3 py-2 text-body"
          data-testid="workspace-environment-unsaved"
        >
          Runtime changes are not durable. Save an environment declaration before the workstation is
          reclaimed.
        </p>
      )}
      <p className="mt-2 text-caption text-muted-foreground">
        Supported fields: immutable image, immutable features, exact-version packages, non-secret
        containerEnv, and one argv postCreateCommand.
      </p>
      <textarea
        className="mt-2 min-h-48 w-full rounded-md bg-muted px-3 py-2 font-mono text-caption"
        aria-label="Environment declaration"
        data-testid="workspace-environment-declaration"
        value={declaration}
        onChange={(event) => setDeclaration(event.target.value)}
        disabled={!availability.available}
      />
      <Button
        className="mt-2"
        disabled={!availability.available || saveEnvironment.isPending}
        data-testid="workspace-save-environment"
        onClick={() => void save()}
      >
        Save environment
      </Button>
      {availability.refusal && (
        <div className="mt-2 text-body" data-testid="workspace-environment-refusal">
          <p className="font-medium text-destructive">
            {displayValue(availability.refusal.reason)}
          </p>
          <p className="text-muted-foreground">{availability.refusal.message}</p>
        </div>
      )}
      {outcome && (
        <p className="mt-2 text-body" data-testid="workspace-environment-outcome">
          {outcome}
        </p>
      )}
    </div>
  );
}

function WorkspaceLocalHistory({ projection }: { projection: WorkspaceProjection }) {
  const workspaceId = projection.workspace_id;
  const {
    data: sessions,
    isLoading: sessionsLoading,
    error: sessionsError,
  } = useSessions(workspaceId);
  const deviceId = projection.workstation.device_id ?? projection.binding?.device_id;
  const sessionId = deviceId
    ? sessions?.find((session) => session.device_id === deviceId)?.id
    : undefined;
  const [entries, setEntries] = useState<VersionEntry[]>([]);
  const [message, setMessage] = useState('Saved version');
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!sessionId) throw new Error('Version history requires an active session on this device');
    const history = await getSDK().workspaces.listHistory(workspaceId, {
      session_id: sessionId,
      limit: 20,
    });
    setEntries(history.entries);
  }, [sessionId, workspaceId]);

  useEffect(() => {
    if (!sessionId) return;
    void refresh().catch((historyError) => setOutcome(mutationMessage(historyError)));
  }, [refresh, sessionId]);

  const execute = useCallback(
    async (action: () => Promise<unknown>, success: string) => {
      setBusy(true);
      setOutcome(null);
      try {
        await action();
        await refresh();
        setOutcome(success);
      } catch (historyError) {
        setOutcome(mutationMessage(historyError));
      } finally {
        setBusy(false);
      }
    },
    [refresh]
  );

  if (sessionsLoading) {
    return (
      <div className="mt-4 pt-4" data-testid="workspace-local-history-loading">
        <h4 className="text-label font-semibold">Version history</h4>
        <p className="mt-2 text-body text-muted-foreground">Finding an eligible local session…</p>
      </div>
    );
  }

  if (sessionsError || !sessionId) {
    return (
      <div className="mt-4 pt-4" data-testid="workspace-local-history-unavailable">
        <h4 className="text-label font-semibold">Version history unavailable</h4>
        <p className="mt-2 text-body text-muted-foreground">
          {sessionsError
            ? sessionsError.message
            : 'Open this workspace in chat on its assigned device before saving or restoring versions.'}
        </p>
      </div>
    );
  }

  return (
    <div className="mt-4 pt-4" data-testid="workspace-local-history">
      <h4 className="text-label font-semibold">Version history</h4>
      <div className="mt-2 flex gap-2">
        <input
          className="min-w-0 flex-1 rounded-md bg-muted px-3 py-2 text-body"
          aria-label="Version name"
          data-testid="workspace-version-message"
          value={message}
          onChange={(event) => setMessage(event.target.value)}
        />
        <Button
          disabled={busy || !message.trim()}
          data-testid="workspace-save-version"
          onClick={() =>
            void execute(
              () =>
                getSDK().workspaces.saveVersion(workspaceId, {
                  session_id: sessionId,
                  message: message.trim(),
                }),
              'Version saved.'
            )
          }
        >
          Save version
        </Button>
      </div>
      {entries.length === 0 ? (
        <p className="mt-2 text-body text-muted-foreground">No versions yet.</p>
      ) : (
        <ul className="mt-2 space-y-2" data-testid="workspace-local-history-list">
          {entries.map((entry) => (
            <li
              key={entry.version}
              className="flex items-center justify-between gap-2 rounded-md p-2"
            >
              <span className="text-body">{entry.message}</span>
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                data-testid={`workspace-restore-version-${entry.version.slice(0, 12)}`}
                onClick={() =>
                  void execute(
                    () =>
                      getSDK().workspaces.restoreVersion(workspaceId, {
                        session_id: sessionId,
                        version: entry.version,
                        message: `Restore ${entry.message}`,
                      }),
                    `Restored ${entry.message}.`
                  )
                }
              >
                Restore
              </Button>
            </li>
          ))}
        </ul>
      )}
      {outcome && (
        <p className="mt-2 text-body" data-testid="workspace-history-outcome">
          {outcome}
        </p>
      )}
    </div>
  );
}
